import React, { useState, useRef } from 'react';
import { Form, Button, FormGroup, Input, Label, Table, Modal, ModalHeader, ModalBody, ModalFooter, Progress } from 'reactstrap';

import authorizedFetch, { authorizedPost } from '../api-authorization/AuthorizedFetch';
import WithLoader from '../WithLoader';
import { safeParseResponse } from '../../utils';

/* EXISTING STATE:
    

*/

const parseContents = content => {
  const lines = content.split("\n");

  const headerColumns = lines[0].split(',').map(x => x.trim().toLowerCase());
  const indices = {
    firstName: headerColumns.indexOf('first name'),
    lastName: headerColumns.indexOf('last name'),
    password: headerColumns.indexOf('password'),
    email: headerColumns.indexOf('email')
  };
  
  return lines.slice(1).map(x => {
    const columns = x.split(',');

    return {
      firstName: columns[indices.firstName],
      lastName: columns[indices.lastName],
      password: columns[indices.password],
      email: columns[indices.email],
      role: 'Student'
    };
  });
};

const ImportStudentsModal = ({ isOpen, toggle, studentCreator, onImported }) => {
  const fileRef = useRef();
  const [loadingState, setLoadingState] = useState({ isLoading: false, usersCreated: 0, usersToCreate: 0 });

  const importStudents = () => {
    const file = fileRef?.current?.files?.[0];

    if(!file){
      return;
    }

    var reader = new FileReader();
    reader.onload = async function(e) {
      var contents = e.target.result;
      const createUserObjects = parseContents(contents) || [];

      setLoadingState({
          isLoading: true,
          usersCreated: 0,
          usersToCreate: createUserObjects?.length
      });

      for (const user of createUserObjects) {
        await new Promise(resolve => setTimeout(resolve, 100));
        await studentCreator(user);
        setLoadingState((s) => {
          return {
            ...s,
            usersCreated: s.usersCreated + 1
          };
        });
      }

      onImported && onImported();
    }

    reader.readAsText(file);
  };

  return <Modal isOpen={isOpen} toggle={toggle}>
    <ModalHeader toggle={toggle}>
      Import Students
    </ModalHeader>
    <ModalBody>
      Import students from a CSV containing columns for First Name, Last Name, Email, and Password.
      <Form>
        <FormGroup>
          <Label>File</Label>
          <Input innerRef={fileRef} type="file" />
        </FormGroup>
      </Form>
      {
        loadingState.isLoading && 
          <Progress animated striped color='information' value={loadingState.usersCreated} max={loadingState.usersToCreate} />
      }
    </ModalBody>
    <ModalFooter>
      <Button
        color="primary"
        onClick={importStudents}
      >
        Import
      </Button>
      <Button onClick={toggle}>
        Cancel
      </Button>
    </ModalFooter>
  </Modal>
}

const AddStudentModal = ({ isOpen, toggle, studentCreator, onAdded }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');

  const addStudent = async () => {
    await studentCreator({
      email,
      firstName,
      lastName,
      password,
      role: 'Student'
    });

    if(onAdded)
    {
      onAdded();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Add Student
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label>Student Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="abc@example.com" />
          </FormGroup>
          <FormGroup>
            <Label>First Name</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
          </FormGroup>
          <FormGroup>
            <Label>Last Name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" />
          </FormGroup>
          <FormGroup>
            <Label>Password</Label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={addStudent}
        >
          Create
        </Button>
        <Button onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const ResetPasswordModal = ({ isOpen, toggle, userId, onReset }) => {
  const passwordRef = useRef();

  const resetPassword = async () => {
    const password = passwordRef.current?.value;
    await authorizedPost(`api/users/${userId}/password`, password);
    onReset && onReset();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        Reset Password
      </ModalHeader>
      <ModalBody>
        Reset a user's password.
        <Form>
          <FormGroup>
            <Label>New Password</Label>
            <Input innerRef={passwordRef} type="password" />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={resetPassword}
        >
          Reset
        </Button>
        <Button onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const StudentPanel = ({ schoolId }) => {
  const [students, setStudents] = useState([]);
  // todo - do we need this? was unused - also the variable/state named "new" wasn't used anywhere i could tell - also we should avoid that var name since its a JS keyword
  // eric: no, we don't need this
  // const [selectedStaff, setSelectedStaff] = useState(null);
  const [reloadCount, setReloadCount] = useState(0);
  const [modalState, setModalState] = useState(false);
  const [importState, setImportState] = useState(false);
  const [resetResetPasswordModalIsOpen, setResetPasswordModalIsOpen] = useState(false);
  const [userId, setUserId] = useState(null);


  const loadStudents = async done => {
    const response = await authorizedFetch(`api/schools/${schoolId}/students`);
    const { students } = await safeParseResponse(response) || {};
    setStudents(students);
    done();
  };

  const toggleImport = () => setImportState(!importState);

  const toggleNewStudent = () => setModalState(!modalState);

  const toggleResetPassword = e => {
    setResetPasswordModalIsOpen(!resetResetPasswordModalIsOpen);
    setUserId(e.target?.value);
  }

  const createStudent = async userCommand => {
    // todo - does this need to be .json()'d?
    // eric: it does at some point need to be but first I'm checking for the 400 status, which should return an array of error messages
    const response = await authorizedPost(`api/schools/${schoolId}/students`, userCommand) || {};
    const { status } = response;

    if(status === 400) {
      return {
        // todo now - figure out how "messages" is used, then give fallback value for safeparseresponse
        // eric: messages might not be used, should be containing validation errors though, possibly for display somewhere
        messages: await safeParseResponse(response),
        status
      }
    }

    return { status };
  };

  const importCompleted = () => {
    toggleImport();
    setReloadCount(reloadCount + 1);
  };

  const studentAdded = () => {
    toggleNewStudent();
    setReloadCount(reloadCount + 1);
  };

  return (
    <WithLoader key={reloadCount} action={loadStudents}>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th style={{ textAlign:'right' }}>
              <Button color='primary' onClick={toggleImport}>Import List</Button>
              <Button color='primary' onClick={toggleNewStudent}>New</Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {students?.map(({ applicationUserId, email, firstName, lastName }) => (
            <tr key={applicationUserId}>
              <td>{firstName} {lastName}</td>
              <td>{email}</td>
              <td style={{ textAlign:'right' }}>
                <Button onClick={toggleResetPassword} value={applicationUserId}>
                  Reset Password
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      
      {importState && (
        <ImportStudentsModal 
          isOpen={importState} 
          toggle={toggleImport} 
          studentCreator={createStudent} 
          onImported={importCompleted} 
        />
      )}

      <ResetPasswordModal 
        isOpen={resetResetPasswordModalIsOpen} 
        toggle={toggleResetPassword} 
        userId={userId} 
        onReset={toggleResetPassword} 
      />

      <AddStudentModal 
        isOpen={modalState} 
        toggle={toggleNewStudent} 
        studentCreator={createStudent} 
        onAdded={studentAdded} 
      />
    </WithLoader>
  );
}

export default StudentPanel;